import { CSSProperties, useEffect, useRef, useState } from 'react';
import { DatePickerComponent, HierarchicalDropdownFieldComponent } from '@vivli/shared/components';
import { ColDef, GridApi } from 'ag-grid-community';
import { first } from 'rxjs/operators';
import { IDropdownMenuItem, IReportItem, IReportSpec, IStatusUpdate } from '@vivli/shared/infrastructure/interface';

import { GridCellRendererEnum, ReportDataTypeEnum } from '@vivli/shared/infrastructure/enum';
import { useDataRequestsApiContext, useToastApiContext } from '@vivli/core/infrastructure/context';
import { useDates, useQueryParamsHook } from '@vivli/shared/infrastructure/hook';
import { useReportItems } from '@vivli/features/dashboard/infrastructure/hook';
import moment from 'moment';
import { StudyStatusEnum } from '@vivli/features/studies/infrastructure/enum';
import { MetricsReportFilterComponent } from '@vivli/features/dashboard/components';
import { GridPageView } from '@vivli/shared/views';

const customHeaderContainerStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(300px, auto)',
    columnGap: '10px',
    alignItems: 'end'
};

const adminReportStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 300px',
    columnGap: '10px',
    alignItems: 'end'
};

// Shape of the date strings the API emits for report columns marked cellDataType "dateString"
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const ReportFeature = () => {
    const dataRequestsService = useDataRequestsApiContext();
    const toast = useToastApiContext();
    const { getSubheader } = useReportItems();

    const {
        reportItems,
        isLoadingReportItems,
        reportSpecs,
        refreshItemsForSpecificReport,
        vivliAdminReport,
        getDataRequestUrlForReportItem,
        getDisclosureUrlForReportItem
    } = useReportItems();
    const [selectedReportSpec, setSelectedReportSpec] = useState<IReportSpec>();
    const [reportSpecMenuItems, setReportSpecMenuItems] = useState<IDropdownMenuItem[]>();
    const [generatedColumnDefs, setGeneratedColumnDefs] = useState<ColDef[]>();
    const [dateForCalculations, setDateForCalculations] = useState<Date>();
    const [dataRequestIds, setDataRequestIds] = useState([]);
    const [reportTitle, setReportTitle] = useState('');
    const gridRef = useRef<GridApi>();
    const [selectedOrg, setSelectedOrg] = useState<string>(null);
    const queryParms = useQueryParamsHook();
    const { formatDate } = useDates();
    const subheader = getSubheader();

    const getQueryString = () => {
        let paramArray = [];

        if (dataRequestIds?.length) {
            // Padded with zeros to make it 8 digits
            const paddedIds = dataRequestIds.map((id) => id.padStart(8, '0'));
            paramArray = [...paramArray, ...paddedIds.map((id) => `dataRequestId=${id}`)];
        }
        return paramArray.length ? paramArray.join('&') : null;
    };

    const saveStatusUpdate = (checked: boolean, data: IReportItem) => {
        const newStatus: IStatusUpdate = {
            ...data.statusUpdate,
            flagRequest: checked
        };

        dataRequestsService
            .updateDataRequestStatus(data.realId, newStatus)
            .pipe(first())
            .subscribe(
                () => {
                    toast.success('Successfully saved your Status Update', {
                        autoClose: 3000
                    });
                },
                (e) => {
                    toast.error(
                        `An error occurred submitting your status update.  Please try again or contact Vivli support. Message: ${e.message}`,
                        { autoClose: 3000 }
                    );
                }
            );
    };

    // Note: All column specifics for all types of reports are DEFINED in the individual report spec
    // json files in this repo under Vivli.API/StaticDbConfig/simpleObjects/ReportSpecDocument-Files.
    // The static-config deploy (Pipelines/Templates/run-db-config.yml, via Vivli.Common.Tools.Utility
    // DBConfig) loads them into Cosmos as ReportSpecDocument items, which the API reads as configuration
    // when it starts up. The report specs are used to create the column definitions for the reports.
    // To change an existing report, edit its spec json here. To test your change locally before the
    // static-config deploy runs, you can also make the same change manually in the Cosmos document that
    // contains the report (select * from c where c.DocumentType = "ReportSpecDocument") and restart the
    // API; the configuration will then contain your changed record. To add a new report, add a new json
    // file in that directory, following the naming conventions used for the existing reports. After the
    // static-config deploy runs on an environment, restart that environment's API to pick up the changes.
    //
    // Columns marked cellDataType "dateString" carry yyyy-MM-dd strings from the API so ag-grid sorts and
    // filters them as dates; the value formatter below renders them as DD-MMM-yyyy for display and CSV.
    const formatDateStringCell = (value: unknown): string => {
        if (typeof value !== 'string' || !ISO_DATE_PATTERN.test(value)) {
            return value?.toString() ?? '';
        }
        return formatDate(value);
    };

    const generateColumnDefs = (): ColDef[] => {
        const genColumns: ColDef[] = [];
        const reportCol = selectedReportSpec?.reportColumns;
        reportCol.forEach(function (c) {
            const col: ColDef = {
                filter: true,
                sortable: true,
                wrapText: true,
                cellRenderer: null,
                cellRendererParams: null,
                valueGetter: null
            };
            col.headerName = c.columnTitle;
            col.field = c.columnPropertyName;
            col.width = c.width > 0 ? c.width : null;
            if (c.cellRenderer) {
                col.cellRenderer = mapCellRendererTypes(c.cellRenderer.toString());
            }
            if (c.cellRendererParams) {
                col.cellRendererParams = c.cellRendererParams;
            }
            if (col.headerName.length > 12) {
                col.headerTooltip = col.headerName;
            }
            if (c.showLocalDate) {
                col.valueGetter = function (params) {
                    const localizedDate = getLocalDate(params.data[col.field]);
                    return localizedDate;
                };
            }
            if (c.cellDataType) {
                col.cellDataType = c.cellDataType;
            }
            if (c.cellDataType === 'dateString') {
                col.valueFormatter = (params) => formatDateStringCell(params.value);
            }

            if (genColumns.length === 0) {
                //first item has to have pinned attribute
                //forcing the field name (it's always VivliID)
                if (selectedReportSpec?.dataType === ReportDataTypeEnum.DataRequest) {
                    col.field = 'vivliId';
                    col.width = 120;
                }
                if (selectedReportSpec?.dataType === ReportDataTypeEnum.Disclosure) {
                    col.field = 'vivliRequestId';
                    col.width = 120;
                }
                col.pinned = 'left';
                col.wrapText = false;
                col.comparator = (valueA, valueB) => valueA - valueB;
            }
            genColumns.push(col);
        });
        // the last column is hardcoded - vivli admins in non-calc reports only
        if (
            selectedReportSpec?.userType === 'VivliAdmin' &&
            !selectedReportSpec?.isForMetricsCalculations &&
            selectedReportSpec?.dataType === ReportDataTypeEnum.DataRequest
        ) {
            genColumns.push({
                field: 'statusUpdate.flagRequest',
                headerName: 'Flag',
                filter: true,
                sortable: true,
                width: 100,
                cellRenderer: GridCellRendererEnum.GridCellYesNo,
                cellDataType: 'text'
            });
        }

        return genColumns;
    };

    const mapCellRendererTypes = (reportSpecRendererDef: string): GridCellRendererEnum => {
        switch (reportSpecRendererDef) {
            case 'GridCellList':
                return GridCellRendererEnum.GridCellList;
            case 'GridCellLargeText':
                return GridCellRendererEnum.GridCellLargeText;
            case 'GridCellDateTime':
                return GridCellRendererEnum.GridCellDateTime;
            case 'GridCellDate':
                return GridCellRendererEnum.GridCellDate;
            case 'GridCellLongText':
                return GridCellRendererEnum.GridCellLongText;
            case 'GridCellLargeTextScroll':
                return GridCellRendererEnum.GridCellLargeTextScroll;
        }
    };

    const handleRowClick = (data: IReportItem) => {
        if (!selectedReportSpec || !data) {
            return;
        }

        let urlToOpen = null;
        switch (selectedReportSpec?.dataType) {
            case ReportDataTypeEnum.DuaDetail:
            case ReportDataTypeEnum.DataRequest:
                urlToOpen = getDataRequestUrlForReportItem(data);
                break;
            case ReportDataTypeEnum.ResearchTeam:
                urlToOpen = getDataRequestUrlForReportItem(data).replace('StatusUpdate', 'ResearchTeam');
                break;
            case ReportDataTypeEnum.DataSet:
                if (data.status === StudyStatusEnum.Draft) {
                    urlToOpen = `/admin/studies/submitStudy/${data.realId}/details`;
                } else {
                    urlToOpen = `/admin/studies/${data.realId}/admin-study-feature`;
                }
                break;
            case ReportDataTypeEnum.ListingRequest: {
                urlToOpen = `/study-submission/${data.realId}/InformationAboutYourTeam`;
                break;
            }
            case ReportDataTypeEnum.Enquiry: {
                urlToOpen = `/admin/enquiry/${data.realId}`;
                break;
            }
            case ReportDataTypeEnum.Disclosure: {
                urlToOpen = `/disclosure/${data.dataRequestGuid}/${data.disclosureId}`;
                break;
            }
        }

        window.open(urlToOpen);
    };

    const getLocalDate = (date: Date) => {
        const returnVal = date ? moment(date).local().format('YYYY-MM-DD hh:mm A') : null;
        return returnVal;
    };

    const reportNameFunction = (reportName) => {
        let title;
        if (reportName.split(',')[1]) {
            title = reportName.split(',')[0];
        } else {
            title = reportName.split('(')[0];
        }
        if (queryParms['nctId']) {
            title = title.trim().replace(' ', '_') + ` for NCT Id ${queryParms['nctId']}`;
        }
        setReportTitle(title.trim().replace(' ', '_'));
    };

    ///We refresh when the dataType changes, when we go from non-metrics to metrics,
    ///and when we change a data request report from 1 request per row to
    ///1 requested study per row or vice versa
    const handleReportTypeChanged = (value: any) => {
        //TODO - 13046 -add condition when we switch from Research Team Members report and to Research Team Member report
        if (
            selectedReportSpec &&
            selectedReportSpec?.dataType === value.dataType &&
            selectedReportSpec?.reportGenerationType === value.reportGenerationType &&
            selectedReportSpec?.isForMetricsCalculations === value.isForMetricsCalculations
        ) {
            setSelectedReportSpec(value);
            reportNameFunction(value.name);
            return;
        }

        if (value.isForMetricsCalculations === true) {
            refreshItemsForSpecificReport(value.dataType, value.code, null, selectedOrg, getQueryString());
        } else if (value.dataType === ReportDataTypeEnum.Enquiry && queryParms['nctId']) {
            refreshItemsForSpecificReport(value.dataType, value.code, null, queryParms['nctId']);
        } else {
            refreshItemsForSpecificReport(value.dataType, value.code);
        }

        setSelectedReportSpec(value);
        reportNameFunction(value.name);
    };

    const handleDateChanged = (d: Date) => {
        setDateForCalculations(d);
        refreshItemsForSpecificReport(selectedReportSpec?.dataType, selectedReportSpec?.code, d);
    };

    const handleContributorChanged = (option: any) => {
        const thisOrg = option[0].value;
        if (thisOrg === 'all') {
            setSelectedOrg(null);
        } else {
            setSelectedOrg(thisOrg);
        }
        const orgValue = thisOrg === 'all' ? null : thisOrg;

        if (selectedReportSpec?.isForMetricsCalculations) {
            refreshItemsForSpecificReport(
                selectedReportSpec?.dataType,
                selectedReportSpec?.code,
                dateForCalculations,
                orgValue,
                getQueryString()
            );
        }
    };

    useEffect(() => {
        if (!reportSpecs) {
            return;
        }
        const activeReportSpecs = reportSpecs.filter((a) => a.active);
        const menuItems = activeReportSpecs.map(
            ({
                name,
                code,
                dataType,
                active,
                category,
                accessType,
                userType,
                reportColumns,
                reportGenerationType,
                isForMetricsCalculations
            }) => ({
                value: {
                    name,
                    code,
                    dataType,
                    active,
                    category,
                    accessType,
                    userType,
                    reportColumns,
                    reportGenerationType,
                    isForMetricsCalculations
                },
                title: name
            })
        );
        setReportSpecMenuItems(menuItems);
        if (!selectedReportSpec) {
            //set the default report type
            if (queryParms['reportType']) {
                const sel = reportSpecs.find((a) => a.dataType === queryParms['reportType']);
                handleReportTypeChanged(sel);
            } else {
                const sel = reportSpecs.find((a) => a.default);
                setSelectedReportSpec(sel);
                reportNameFunction(sel.name);
            }
        }
    }, [reportSpecs]);

    useEffect(() => {
        gridRef.current?.refreshCells();
    }, [reportItems]);

    const buildCustomHeader = (reportSpecs) => {
        if (reportSpecs) {
            const dateToUse = dateForCalculations ?? moment().toDate();
            return (
                <div style={customHeaderContainerStyle}>
                    {vivliAdminReport ? (
                        <div style={adminReportStyle}>
                            <MetricsReportFilterComponent
                                setDataRequestIds={setDataRequestIds}
                                handleContributorChanged={handleContributorChanged}
                            />
                            <DatePickerComponent
                                style={{
                                    height: '64px'
                                }}
                                defaultValue={dateToUse}
                                onChange={handleDateChanged}
                                label={'Billing Period Effective Date'}
                            />
                        </div>
                    ) : (
                        <div />
                    )}
                    <HierarchicalDropdownFieldComponent
                        dataId={'reportTypePicker'}
                        style={{ height: '64px' }}
                        items={reportSpecMenuItems}
                        sort={'asc'}
                        disabled={isLoadingReportItems}
                        label={'Available Report Types'}
                        defaultValue={selectedReportSpec}
                        objectKey={'name'}
                        onChange={(value) => handleReportTypeChanged(value as IReportSpec)}
                    />
                </div>
            );
        }
    };

    useEffect(() => {
        if (!selectedReportSpec) {
            return;
        }
        const generatedColumns = generateColumnDefs();
        setGeneratedColumnDefs(generatedColumns);
    }, [selectedReportSpec]);

    useEffect(() => {
        if (isLoadingReportItems) {
            gridRef.current?.setGridOption('loading', true);
        } else {
            gridRef.current?.setGridOption('loading', false);
        }
    }, [isLoadingReportItems, generatedColumnDefs]);

    return (
        <GridPageView
            title="Reports"
            subHeader={subheader}
            columnDefs={generatedColumnDefs}
            rowData={reportItems}
            onRowClick={handleRowClick}
            exportTitle={reportTitle + '_Report'}
            customHeader={buildCustomHeader(reportSpecs)}
            onGridReady={(gridApi) => (gridRef.current = gridApi)}
            flushPrevious={true}
            customHeaderHideShowButton={'Filters'}
            ignoreClickForFields={['statusUpdate.flagRequest']}
        />
    );
};
